import type { QueryRowType } from "@rocicorp/zero";
import {
  connectorsByIdentifier,
  connectorTypeOf,
  connectorTypes,
} from "../../shared/connectors";
import {
  type ConnectorWithOAuth,
  isItemVisible,
  targetIsSet,
  targetsMatch,
} from "../../shared/connectors/getTargetValue";
import type { IconName } from "../components/Icon";
import type {
  ConnectorDefinition,
  FieldTarget,
  OAuthItem,
} from "../../shared/connectors/types";
import type { queries } from "../../shared/queries";
import { parseStepConfig, stepDirFromPath } from "../../shared/stepConfig";
import { domainFromUrlPattern } from "../../shared/connectors/urlDomain";

export type Connector = ConnectorWithOAuth;

export type ConnectorInfo = QueryRowType<
  ReturnType<typeof queries.connectedAppsIndex>
>;

// A product's display name from its connector identifier.
export const productLabel = (key: string) =>
  connectorsByIdentifier[key]?.name ?? key;

// Products we can auto-detect for a custom connector: real branded connectors,
// not the generic custom shells (apiKey, customOauth, smtp, …).
const productByDomain = new Map<string, string>();
for (const type of connectorTypes) {
  if (type.tags.includes("custom")) continue;
  for (const variant of type.variants) {
    const domain = domainFromUrlPattern(variant.defaultUrlPattern({}));
    if (domain && !productByDomain.has(domain))
      productByDomain.set(domain, type.identifier);
  }
}

// Only the generic custom shells get their product detected from the URL; every
// other connector already declares what it is through its identifier.
const inferableIdentifiers = new Set(["apiKey", "customOauth"]);

function inferProduct(urlPattern: string | undefined): string | null {
  const domain = urlPattern ? domainFromUrlPattern(urlPattern) : null;
  return (domain && productByDomain.get(domain)) || null;
}

// A connector's product when it isn't its own: detected from the URL domain
// for the generic custom shells, declared by the definition for variants of
// another product (e.g. an MCP twin). Null when the connector stands for its
// product itself or nothing is known.
export function resolveProduct(item: {
  identifier: string;
  urlPattern?: string;
}): string | null {
  if (inferableIdentifiers.has(item.identifier))
    return inferProduct(item.urlPattern);
  const type = connectorTypeOf(item.identifier);
  return type && type.identifier !== item.identifier ? type.identifier : null;
}

// The connector's own type, ignoring any URL-detected product — so a custom
// connector grouped under Slack still reads as "API key".
export function connectorRawTypeLabel(app: {
  identifier: string;
  oauthConnection?: unknown;
}): string {
  return (
    connectorsByIdentifier[app.identifier]?.name ??
    (app.oauthConnection ? "OAuth" : "API key")
  );
}

export function connectorTypeLabel(app: {
  identifier: string;
  urlPattern?: string;
  oauthConnection?: unknown;
}): string {
  const resolved = resolveProduct(app);
  if (resolved) return productLabel(resolved);
  return connectorRawTypeLabel(app);
}

// The connectors-list search contract: a connector matches on its name or its
// type label, so "MCP" finds every MCP server whatever it's named.
export function connectorMatchesQuery(
  app: {
    name: string;
    identifier: string;
    urlPattern?: string;
    oauthConnection?: unknown;
  },
  lowerQuery: string
): boolean {
  return (
    app.name.toLowerCase().includes(lowerQuery) ||
    connectorTypeLabel(app).toLowerCase().includes(lowerQuery)
  );
}

// How the connector authenticates, in the variant picker's vocabulary — so a
// product's default variant reads “User OAuth”, not the bare product name.
export function connectorAuthTypeLabel(app: {
  identifier: string;
  oauthConnection?: unknown;
}): string {
  return (
    connectorsByIdentifier[app.identifier]?.authLabel ??
    connectorRawTypeLabel(app)
  );
}

export function connectorNameWithAuth(app: {
  name: string;
  identifier: string;
  oauthConnection?: unknown;
}): string {
  const auth = connectorAuthTypeLabel(app);
  return auth === app.name ? app.name : `${app.name} · ${auth}`;
}

type HeadCommitApps = {
  headCommit?: {
    appUsages: readonly { connectedAppId: string }[];
  } | null;
};

// Connectors referenced by a commit's steps, deduped (usages are step-keyed, so
// one app can appear across several steps).
export function commitConnectorIds(
  branch: HeadCommitApps | undefined
): string[] {
  return [
    ...new Set(
      (branch?.headCommit?.appUsages ?? []).map((u) => u.connectedAppId)
    ),
  ];
}

// A workflow's live connectors: those at the head of its main branch.
export function mainBranchConnectorIds(
  branches: readonly (HeadCommitApps & { name: string })[]
): string[] {
  const main = branches.find((b) => b.name === "main") ?? branches[0];
  return commitConnectorIds(main);
}

export const usageTypeIcons: Record<
  | "workflow"
  | "personal_chat"
  | "space_git_remote"
  | "audit_log_destination"
  | "ai_provider",
  IconName
> = {
  workflow: "product-workflows",
  personal_chat: "ask",
  space_git_remote: "git-branch",
  audit_log_destination: "output",
  ai_provider: "sparkles",
};

type GrantLike = {
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

type PrincipalHolder = { roleGrantsAsPrincipal: readonly GrantLike[] };

type AppAccessResult =
  | {
      roleGrantsAsScope: readonly {
        userGroup?: PrincipalHolder | undefined;
        user?: PrincipalHolder | undefined;
      }[];
    }
  | null
  | undefined;

export function collectPrincipalGrants(
  connectorAccess: AppAccessResult
): GrantLike[] {
  return (
    connectorAccess?.roleGrantsAsScope.flatMap((g) => [
      ...(g.userGroup?.roleGrantsAsPrincipal ?? []),
      ...(g.user?.roleGrantsAsPrincipal ?? []),
    ]) ?? []
  );
}

export type LiveWorkflow = {
  id: string;
  name: string;
  badgeSeed: string | null;
  spaceId: string;
  draft: boolean;
};

type LiveUsageRow = QueryRowType<
  ReturnType<typeof queries.loadConnectedAppLiveUsage>
>;
type UsageWorkflow = NonNullable<LiveUsageRow["workflow"]>;
type UsageSpace = NonNullable<UsageWorkflow["space"]>;

type StepInfo = {
  stepDir: string;
  color: string;
  workflowId: string;
  workflowName: string;
  spaceId: string;
};

type StepActivityRow = UsageWorkflow["stepActivity"][number];

type BranchUsage = {
  branchId: string;
  branchName: string;
  steps: StepInfo[];
};

export type WorkflowUsage = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  badgeSeed: string | null;
  branches: readonly { name: string }[];
  space: UsageSpace;
  stepActivity: readonly StepActivityRow[];
  usageBranches: BranchUsage[];
};

/** One group per workflow, with its usages bucketed per branch (live first). */
export function groupByWorkflow(
  rows: readonly LiveUsageRow[]
): WorkflowUsage[] {
  const groupMap = new Map<string, WorkflowUsage>();
  for (const row of rows) {
    const wf = row.workflow;
    const usages = row.headCommit?.appUsages ?? [];
    if (!wf?.space || usages.length === 0) continue;

    let group = groupMap.get(wf.id);
    if (!group) {
      group = {
        workflowId: wf.id,
        workflowName: wf.name,
        spaceId: wf.spaceId,
        badgeSeed: wf.badgeSeed,
        branches: wf.branches ?? [],
        space: wf.space,
        stepActivity: wf.stepActivity ?? [],
        usageBranches: [],
      };
      groupMap.set(wf.id, group);
    }

    const colorByDir = new Map<string, string>();
    for (const f of row.headCommit?.files ?? []) {
      const dir = stepDirFromPath(f.path);
      if (dir && f.blob)
        colorByDir.set(dir, parseStepConfig(f.blob.content).color);
    }

    group.usageBranches.push({
      branchId: row.id,
      branchName: row.name,
      steps: usages.map((u) => ({
        stepDir: u.subKey,
        color: colorByDir.get(u.subKey) ?? "sky",
        workflowId: wf.id,
        workflowName: wf.name,
        spaceId: wf.spaceId,
      })),
    });
  }
  for (const group of groupMap.values()) {
    group.usageBranches.sort((a, b) =>
      a.branchName === "main"
        ? -1
        : b.branchName === "main"
          ? 1
          : a.branchName.localeCompare(b.branchName)
    );
  }
  return [...groupMap.values()];
}

export function liveWorkflows(
  rows: readonly LiveUsageRow[]
): Array<LiveWorkflow> {
  const seen = new Map<string, LiveWorkflow>();
  for (const row of rows) {
    const workflow = row.workflow;
    if (!workflow) continue;
    const existing = seen.get(workflow.id);
    if (existing) {
      if (row.name === "main") existing.draft = false;
    } else {
      seen.set(workflow.id, {
        id: workflow.id,
        name: workflow.name,
        badgeSeed: workflow.badgeSeed,
        spaceId: workflow.spaceId,
        draft: row.name !== "main",
      });
    }
  }
  return Array.from(seen.values());
}

const dependencyIsHidden = (
  target: FieldTarget,
  ca: Connector,
  definition: ConnectorDefinition
) => {
  const item = definition.steps
    .flatMap((step) => step.items)
    .find((i) => "target" in i && targetsMatch(i.target, target));
  return item !== undefined && !isItemVisible(item, ca, definition);
};

export const unsatisfiedDependencies = (
  ca: Connector,
  item: OAuthItem,
  definition: ConnectorDefinition
) =>
  (item.dependsOn ?? []).filter(
    (target) =>
      !targetIsSet(target, ca) && !dependencyIsHidden(target, ca, definition)
  );

// Split a step's connectors into the ones that get a live, manageable chip and
// the ones that need the faded "reconnect" treatment (missing or unreadable).
export function splitStepConnectors<
  App extends { id: string },
  Row extends { connectedApp: { id: string } | null; connectedAppId: string },
>(
  stepConnectors: readonly Row[],
  allConnectors: readonly App[]
): { connected: App[]; associatedIds: string[]; disconnected: Row[] } {
  const associatedIdSet = new Set(stepConnectors.map((r) => r.connectedAppId));
  const connected: App[] = [];
  const associatedIds: string[] = [];
  for (const app of allConnectors) {
    if (!associatedIdSet.has(app.id)) continue;
    associatedIds.push(app.id);
    connected.push(app);
  }
  const disconnected = stepConnectors.filter((r) => !r.connectedApp);
  return { connected, associatedIds, disconnected };
}

export const connectorGroupKey = (item: {
  identifier: string;
  name: string;
  urlPattern?: string;
}) => {
  const resolved = resolveProduct(item);
  if (resolved) return resolved;
  return item.identifier === "apiKey" || item.identifier === "customOauth"
    ? `${item.identifier}-${item.name}`
    : item.identifier;
};

export const uniqueByType = <T extends { identifier: string; name: string }>(
  list: T[]
) => {
  const seen: string[] = [];
  const result: T[] = [];
  list.forEach((item) => {
    const reference = connectorGroupKey(item);
    if (!seen.includes(reference)) {
      seen.push(reference);
      result.push(item);
    }
  });
  return result;
};

// The ConnectorAvatars `connected` items for a set of connected-app ids:
// resolve each against the index, drop unknown ids, and collapse to one avatar
// per connector type.
export function connectorAvatarItems(
  ids: readonly string[],
  connectorById: ReadonlyMap<string, ConnectorInfo>
): { id: string; name: string; app: ConnectorInfo }[] {
  return uniqueByType(
    ids
      .map((id) => connectorById.get(id))
      .filter((ca): ca is ConnectorInfo => ca != null)
  ).map((ca) => ({ id: ca.id, name: ca.name, app: ca }));
}
